import React, { useMemo, useState, useCallback } from 'react';
import { Card, Select, Progress, Table } from 'antd';
import { TopMerchant } from '../../services/dashboardService';
import { formatVnd } from '../../utils/formatVnd';
import { useNavigate } from 'react-router-dom';
import { DashboardCardHeader } from './DashboardCardHeader';
import { DashboardEmptyState } from './DashboardEmptyState';
import { ChartSkeleton } from './ChartSkeleton';

interface TopMerchantsProps {
  data: TopMerchant[];
  loading?: boolean;
  startDate?: string;
  endDate?: string;
}

type SortOption = 'revenue-desc' | 'revenue-asc' | 'orders-desc' | 'orders-asc';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'revenue-desc', label: 'Doanh thu (Cao → Thấp)' },
  { value: 'revenue-asc', label: 'Doanh thu (Thấp → Cao)' },
  { value: 'orders-desc', label: 'Đơn hàng (Cao → Thấp)' },
  { value: 'orders-asc', label: 'Đơn hàng (Thấp → Cao)' },
];

export const TopMerchants: React.FC<TopMerchantsProps> = ({
  data,
  loading = false,
  startDate,
  endDate,
}) => {
  const [sort, setSort] = useState<SortOption>('revenue-desc');
  const navigate = useNavigate();

  const { sortedData, totalRevenue } = useMemo(() => {
    const raw = data || [];
    const total = raw.reduce((s, m) => s + m.revenue, 0);
    const sorted = [...raw].sort((a, b) => {
      switch (sort) {
        case 'revenue-desc':
          return b.revenue - a.revenue;
        case 'revenue-asc':
          return a.revenue - b.revenue;
        case 'orders-desc':
          return b.orders - a.orders;
        case 'orders-asc':
          return a.orders - b.orders;
        default:
          return b.revenue - a.revenue;
      }
    });
    return {
      sortedData: sorted.slice(0, 10),
      totalRevenue: total,
    };
  }, [data, sort]);

  const handleRowClick = useCallback(
    (record: TopMerchant) => {
      navigate(`/merchants/${record.id}`);
    },
    [navigate]
  );

  if (loading) {
    return (
      <Card className="admin-top-merchants-card">
        <DashboardCardHeader title="Top Merchants" subtitle="Theo doanh thu" />
        <ChartSkeleton height={320} />
      </Card>
    );
  }

  return (
    <Card className="admin-top-merchants-card">
      <DashboardCardHeader
        title="Top Merchants"
        subtitle={
          startDate && endDate
            ? `${startDate} – ${endDate}`
            : 'Theo doanh thu trong range'
        }
        actions={
          <Select
            value={sort}
            onChange={v => setSort(v as SortOption)}
            options={SORT_OPTIONS}
            size="small"
            className="top-merchants-sort-select"
            style={{ minWidth: 180 }}
          />
        }
      />
      {sortedData.length === 0 ? (
        <DashboardEmptyState
          message="Chưa có dữ liệu merchant trong khoảng thời gian đã chọn."
          suggestion="Thử mở rộng phạm vi ngày."
        />
      ) : (
        <Table
          dataSource={sortedData}
          rowKey="id"
          pagination={false}
          size="small"
          showHeader={true}
          onRow={record => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: '#',
              key: 'rank',
              width: 36,
              align: 'center',
              render: (_: unknown, __: TopMerchant, idx: number) => idx + 1,
            },
            {
              title: 'Merchant',
              dataIndex: 'name',
              key: 'name',
              ellipsis: true,
              render: (name: string) => (
                <span className="top-merchant-name" title={name}>
                  {name}
                </span>
              ),
            },
            {
              title: 'Doanh thu',
              dataIndex: 'revenue',
              key: 'revenue',
              width: 100,
              align: 'right',
              render: (v: number) => formatVnd(v),
            },
            {
              title: 'Đơn',
              dataIndex: 'orders',
              key: 'orders',
              width: 56,
              align: 'right',
            },
            {
              title: 'Share',
              key: 'share',
              width: 80,
              render: (_: unknown, record: TopMerchant) => {
                const pct =
                  totalRevenue > 0
                    ? Math.round((record.revenue / totalRevenue) * 100)
                    : 0;
                return (
                  <Progress
                    percent={pct}
                    size="small"
                    showInfo={true}
                    format={n => `${n}%`}
                  />
                );
              },
            },
          ]}
        />
      )}
    </Card>
  );
};
